/**
 * One way to turn any failure into words a person can act on.
 *
 * Every screen used to print axios's own `error.message` ("Request failed with
 * status code 500", "Network Error") or a raw server string. That reads like a
 * developer log, tells a crew lead to fix their signal when the server is at
 * fault, and offers "Retry" for a permission problem retrying cannot fix.
 *
 * `describeError()` classifies the failure and returns a title + message in the
 * house voice (docs/product/ux-voice.md: "[Couldn't / Can't] {do the thing}. {Why}.
 * {What to do next}.") plus whether a retry can help. `humanError()` is the
 * one-line string form for toasts and inline errors.
 *
 * It accepts anything a fetch path can throw or hold:
 *   - an AxiosError (apiClient / React Query hooks)
 *   - a native fetch TypeError ("Failed to fetch")
 *   - a plain Error, or the string a legacy `useApiData` hook stored
 *   - an `{ status }`-shaped object
 */

export type ErrorKind =
  | 'offline'      // no network: the request never reached the server
  | 'timeout'      // request took too long (weak signal)
  | 'forbidden'    // 403: the role can't do this; retry won't help
  | 'unauthorized' // 401: signed out
  | 'notFound'     // 404: deleted or wrong link
  | 'conflict'     // 409: someone else changed it
  | 'validation'   // 400/422: the server rejected the input
  | 'rateLimited'  // 429
  | 'server'       // 5xx
  | 'unknown'

export interface DescribedError {
  kind: ErrorKind
  /** HTTP status when there was a response. */
  status: number | null
  /** Short heading: "Couldn't load your invoices". */
  title: string
  /** One sentence: why + what to do. Never a raw status code or stack text. */
  message: string
  /** False for forbidden/notFound/validation: a Try again button would be a dead end. */
  retryable: boolean
}

export interface DescribeOptions {
  /**
   * What the user was doing, as a verb phrase after "Couldn't": "load your
   * invoices", "save business hours", "send the quote". Defaults to
   * "load this" / "finish that".
   */
  action?: string
  /**
   * The thing a forbidden user can't see, after "Your role can't …":
   * "see jobs", "change workspace settings". Defaults to "do this".
   */
  forbiddenWhat?: string
  /** Who to ask for access. Defaults to "an admin". */
  askWho?: string
}

type ErrorLike = {
  name?: string
  code?: string
  message?: string
  status?: number
  response?: { status?: number; data?: unknown }
  request?: unknown
  isAxiosError?: boolean
}

/** Status-code fragments that must never reach a person. */
const RAW_STATUS_RE = /request failed with status code (\d{3})/i
const NETWORK_RE = /network ?error|failed to fetch|load failed|networkerror|err_internet_disconnected|err_network/i
const TIMEOUT_RE = /timeout|timed out|econnaborted/i
/** Internal vocabulary in any casing ("PrismaClientKnownRequestError", "ZodError"). */
const INTERNAL_WORDS_RE = /prisma|exception|zod|stack ?trace|econnrefused|typeerror|referenceerror|sqlstate/i
/** Server strings that are safe to show for a 4xx (short, human, no internals). */
const SAFE_MESSAGE_MAX = 160
const INTERNAL_RE = /(^(body|querystring|params|headers)[/.]|\bmust (be|have|match|NOT|equal)\b|prisma|stack|exception|sql|select |insert |update |delete from|\bat \w+ \(|\/src\/|node_modules|ECONN|ENOTFOUND|undefined|null|NaN|\{|\}|zod|schema|enum|_id\b|[A-Z]{3,}_[A-Z]{2,})/

function isBrowserOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false
}

/** HTTP status behind a failure, or null when no response arrived. */
export function errorStatusOf(err: unknown): number | null {
  if (!err || typeof err !== 'object') {
    if (typeof err === 'string') {
      const m = err.match(RAW_STATUS_RE)
      return m ? Number(m[1]) : null
    }
    return null
  }
  const e = err as ErrorLike
  if (typeof e.response?.status === 'number') return e.response.status
  if (typeof e.status === 'number') return e.status
  const m = typeof e.message === 'string' ? e.message.match(RAW_STATUS_RE) : null
  return m ? Number(m[1]) : null
}

/** Server-provided message, only when it is short and reads as human text. */
function safeServerMessage(err: unknown): string | null {
  const data = (err as ErrorLike)?.response?.data as { error?: unknown; message?: unknown } | undefined
  const raw = typeof data?.error === 'string' ? data.error : typeof data?.message === 'string' ? data.message : null
  if (!raw) return null
  const s = raw.trim()
  if (!s || s.length > SAFE_MESSAGE_MAX) return null
  if (RAW_STATUS_RE.test(s) || INTERNAL_RE.test(s) || INTERNAL_WORDS_RE.test(s)) return null
  if (/^(forbidden|unauthorized|not found|bad request|internal server error|error)\.?$/i.test(s)) return null
  return /[.!?]$/.test(s) ? s : `${s}.`
}

export function classifyError(err: unknown): ErrorKind {
  if (err == null) return 'unknown'
  const status = errorStatusOf(err)
  if (status != null) {
    if (status === 401) return 'unauthorized'
    if (status === 403) return 'forbidden'
    if (status === 404) return 'notFound'
    if (status === 409) return 'conflict'
    if (status === 429) return 'rateLimited'
    if (status === 400 || status === 422) return 'validation'
    if (status === 408) return 'timeout'
    if (status >= 500) return 'server'
    return 'unknown'
  }
  const e = (typeof err === 'object' ? err : { message: String(err) }) as ErrorLike
  const msg = `${e.code ?? ''} ${e.message ?? ''}`
  if (TIMEOUT_RE.test(msg)) return isBrowserOffline() ? 'offline' : 'timeout'
  // Axios sets `request` but no `response` when the network dropped.
  if (NETWORK_RE.test(msg) || (e.isAxiosError && !e.response) || e instanceof TypeError) return 'offline'
  if (/forbidden|not authorized|permission/i.test(msg)) return 'forbidden'
  if (isBrowserOffline()) return 'offline'
  return 'unknown'
}

/** Classify a failure and phrase it for a person. */
export function describeError(err: unknown, opts: DescribeOptions = {}): DescribedError {
  const kind = classifyError(err)
  const status = errorStatusOf(err)
  const action = opts.action ?? 'load this'
  const ask = opts.askWho ?? 'an admin'
  const title = `Couldn't ${action}`

  switch (kind) {
    case 'offline':
      return { kind, status, title: "You're offline", message: `Couldn't ${action}. Check your signal; we'll try again when you're back online.`, retryable: true }
    case 'timeout':
      return { kind, status, title, message: 'The connection is slow right now. Try again in a moment.', retryable: true }
    case 'forbidden':
      return {
        kind, status,
        title: "You don't have access",
        message: `Your role can't ${opts.forbiddenWhat ?? 'do this'}. Ask ${ask} if you need access.`,
        retryable: false,
      }
    case 'unauthorized':
      return { kind, status, title: 'Your session ended', message: 'Sign in again to keep going.', retryable: false }
    case 'notFound':
      return { kind, status, title: "This isn't here anymore", message: 'It may have been deleted, or the link is out of date.', retryable: false }
    case 'conflict':
      return { kind, status, title, message: safeServerMessage(err) ?? 'Someone else changed this at the same time. Refresh and try again.', retryable: true }
    case 'validation':
      return { kind, status, title, message: safeServerMessage(err) ?? 'Some details need another look. Check the highlighted fields.', retryable: false }
    case 'rateLimited':
      return { kind, status, title, message: 'Too many requests just now. This will clear in about a minute.', retryable: true }
    case 'server':
      return { kind, status, title, message: 'Something went wrong on our side. Try again in a minute.', retryable: true }
    default: {
      const own = safeServerMessage(err)
      return { kind, status, title, message: own ?? 'Something went wrong. Try again in a minute.', retryable: true }
    }
  }
}

/**
 * One-line human message for toasts and inline errors:
 *   humanError(err, 'save business hours')
 *   → "Couldn't save business hours. Something went wrong on our side. Try again in a minute."
 * Forbidden/offline/not-found use their own wording without the prefix.
 */
export function humanError(err: unknown, action?: string, opts: Omit<DescribeOptions, 'action'> = {}): string {
  const d = describeError(err, { ...opts, action })
  if (d.kind === 'forbidden' || d.kind === 'unauthorized' || d.kind === 'offline') return d.message
  if (d.kind === 'notFound') return `${d.title}. ${d.message}`
  return action ? `Couldn't ${action}. ${d.message}` : d.message
}

/** True when the failure is the user's role, so the UI should show the access card, not Retry. */
export function isForbiddenError(err: unknown): boolean {
  return classifyError(err) === 'forbidden'
}

/** True when the failure means the device has no network (not a server fault). */
export function isNetworkError(err: unknown): boolean {
  const k = classifyError(err)
  return k === 'offline'
}
